/**
 * circle-exam — 圓考卷(第 6 單元 圓)線上互動卷 + 純計分函式。
 *
 * 對應 mul-div-exam.ts / time-exam.ts / stats-table-exam.ts 同一套形狀:分大題作答 → 交卷
 * 即時計分 + 逐題對錯 + 正解 + 錯題回流。純計分 `scoreCircleExam` / 答案正規化
 * `normalizeCircleAnswer` 抽出可單測(無 DOM 依賴)。
 *
 * 題型差異:
 *   - convert / compass(換算 / 圓規概念)= 文字輸入,寬鬆比對(全形→半形、去空白、單位寬鬆)。
 *   - term / judge(名詞 / 判斷)= 選項按鈕(點選即作答),答案 = 選項字串。
 *   - construct(圓規操作)= **printOnly**:線上不批改、不計分,只顯示「請印出用圓規做」+ 解答提示。
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕。
 * 專業介面鐵則:無音效、無 mascot、無彩光、無戲謔文案。
 *
 * 學習/遊戲分離:純學習側,只動學習數據,**絕不**碰 commanderXp / 戰鬥數值。
 */

import type { CircleExam, CircleSection, CircleQuestion } from './circle-gen';
import { pageShell, academicButton } from '../ui/components';

// ───────────────────────────── 答案正規化 + 比對 ─────────────────────────────

/**
 * 正規化一個圓答案字串以利寬鬆比對:
 *   - 全形數字 → 半形。
 *   - 去所有空白。
 *   - 「公分」/「cm」尾綴去除(只比數值;名詞 / 對錯不受影響)。
 * 例:'8 公分' / '８公分' / '8cm' → '8';'對' → '對';'半徑' → '半徑'。
 */
export function normalizeCircleAnswer(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();
  // 全形數字 → 半形。
  s = s.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xff10 + 0x30));
  // 全形英文字母 → 半形(供 cm)。
  s = s.replace(/[Ａ-Ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff21 + 0x41));
  s = s.replace(/[ａ-ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff41 + 0x61));
  // 去單位「公分」/「cm」(大小寫不敏感)。
  s = s.replace(/公分/g, '').replace(/cm/gi, '');
  // 去所有空白。
  s = s.replace(/\s+/g, '');
  return s;
}

/**
 * 一格是否答對(寬鬆比對):
 *   1. 名詞 / 判斷題(正解為純中文「圓心/半徑/直徑/圓周/對/錯」)→ 去空白後整段相符。
 *   2. 換算 / 圓規題(正解含數值 + 公分)→ 去單位後數值相符(學生寫「8」或「8 公分」都算對)。
 */
export function isCircleAnswerCorrect(studentRaw: string | undefined, correct: string): boolean {
  if (studentRaw === undefined) return false;
  const s = normalizeCircleAnswer(studentRaw);
  const c = normalizeCircleAnswer(correct);
  if (s === '') return false;
  return s === c;
}

// ───────────────────────────── 純計分 ─────────────────────────────

/** 學生整卷作答:`"sectionIdx:questionIdx" → 字串`。 */
export type CircleExamAnswers = Record<string, string>;

/** 單題判定結果。 */
export interface CircleItemResult {
  itemId: string;
  correct: boolean;
  /** 正解顯示字串。 */
  correctLabel: string;
  /** 學生作答顯示字串(未作答 = 空)。 */
  chosenLabel: string;
  points: number;
  earned: number;
}

/** 整卷計分結果。 */
export interface CircleExamScore {
  score: number;
  total: number;
  correctCount: number;
  itemCount: number;
  results: CircleItemResult[];
}

/**
 * 純計分:逐題批改(每題一格)。可單測(無 DOM 依賴)。
 * **printOnly 大題(construct 圓規操作)整段略過**(線上不批改、不計分、不入總分)。
 *
 * @param exam    一份圓考卷。
 * @param answers 整卷作答(`"sectionIdx:questionIdx" → 字串`)。
 * @returns 得分 / 滿分 / 逐題結果(不含 printOnly 大題)。
 */
export function scoreCircleExam(exam: CircleExam, answers: CircleExamAnswers): CircleExamScore {
  const results: CircleItemResult[] = [];
  let score = 0;
  let total = 0;
  let correctCount = 0;
  let itemCount = 0;

  exam.sections.forEach((section, sIdx) => {
    if (section.printOnly) return; // 圓規操作題線上不批改。
    section.questions.forEach((q, qIdx) => {
      itemCount += 1;
      total += section.pointsEach;
      const studentRaw = answers[`${sIdx}:${qIdx}`];
      const ok = isCircleAnswerCorrect(studentRaw, q.answer);
      const earned = ok ? section.pointsEach : 0;
      if (ok) {
        correctCount += 1;
        score += section.pointsEach;
      }
      results.push({
        itemId: q.id,
        correct: ok,
        correctLabel: q.answer,
        chosenLabel: studentRaw && studentRaw.trim() !== '' ? studentRaw.trim() : '',
        points: section.pointsEach,
        earned,
      });
    });
  });

  return { score, total, correctCount, itemCount, results };
}

// ───────────────────────────── 互動卷 UI ─────────────────────────────

/** 交卷結果回呼:錯題(供回流錯題本)。 */
export interface CircleExamWrong {
  question: CircleQuestion;
  sectionKind: CircleSection['kind'];
}

/**
 * Render 線上圓考卷 into `container`。每大題列題 + 填答格 / 選項 → 「交卷」即時計分 + 逐題對錯。
 * printOnly 大題(圓規操作)只顯示題目 + 「請印出用圓規做」提示,不放作答格、不計分。
 *
 * @param container 掛載點。
 * @param exam      一份(產生)圓考卷。
 * @param onDone    離開 / 完成回呼。
 * @param onSubmit  選填:交卷後回呼(帶分數 + 錯題),供錯題回流。
 */
export function runCircleExam(
  container: HTMLElement,
  exam: CircleExam,
  onDone: () => void,
  onSubmit?: (score: CircleExamScore, wrong: CircleExamWrong[]) => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: `${exam.scope} · 在每格寫出答案或點選選項,作答後按「交卷」看分數與正解。`,
    onBack: onDone,
    confirmOnBack: '離開後這張卷的作答不會保留,確定?',
  });

  const root = document.createElement('div');
  root.className = 'circle-exam';
  root.setAttribute('data-testid', 'circle-exam');
  root.style.cssText = 'display:flex;flex-direction:column;gap:22px;';
  shell.body.appendChild(root);

  const answers: CircleExamAnswers = {};
  let submitted = false;

  exam.sections.forEach((section, sIdx) => {
    const sectionEl = document.createElement('section');
    sectionEl.className = `cir-section cir-section--${section.kind}`;
    sectionEl.setAttribute('data-testid', `cir-section-${section.kind}`);
    sectionEl.style.cssText = 'display:flex;flex-direction:column;gap:14px;';

    const head = document.createElement('div');
    head.className = 'cir-section-head';
    head.style.cssText =
      'font-family:var(--ef-serif);font-weight:700;font-size:18px;color:var(--ef-ink);' +
      'border-left:4px solid var(--ef-accent,#2f6b4f);padding-left:10px;';
    head.textContent = `${section.label}、${section.title}`;
    sectionEl.appendChild(head);

    const instr = document.createElement('div');
    instr.className = 'cir-section-instr';
    instr.style.cssText = 'font-size:14px;color:var(--ef-sub);line-height:1.6;';
    instr.textContent = section.instructions;
    sectionEl.appendChild(instr);

    if (section.printOnly) {
      // 圓規操作題:只列題 + 提示,不放作答格、不計分。
      const note = document.createElement('div');
      note.className = 'cir-printonly-note';
      note.setAttribute('data-testid', 'cir-printonly-note');
      note.style.cssText =
        'font-size:13px;color:#8a6d2f;background:#fbf6e6;border:1px dashed #cbb46a;' +
        'border-radius:6px;padding:8px 10px;';
      note.textContent = '這一大題請點「列印考卷」印出,用圓規實際畫圓,線上不批改。';
      sectionEl.appendChild(note);
      section.questions.forEach((q, qIdx) => {
        sectionEl.appendChild(renderPrintOnlyQuestion(qIdx, q));
      });
    } else {
      section.questions.forEach((q, qIdx) => {
        sectionEl.appendChild(renderQuestion(sIdx, qIdx, q, answers, section.kind));
      });
    }

    root.appendChild(sectionEl);
  });

  // ── 交卷 + 結果區 ──
  const actions = document.createElement('div');
  actions.className = 'cir-actions';
  actions.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;margin-top:8px;';
  root.appendChild(actions);

  const resultEl = document.createElement('div');
  resultEl.className = 'cir-result';
  resultEl.style.cssText = 'display:none;flex-direction:column;gap:10px;';
  root.appendChild(resultEl);

  const submitBtn = academicButton({
    label: '交卷',
    variant: 'primary',
    onClick: () => {
      if (submitted) return;
      submitted = true;
      const result = scoreCircleExam(exam, answers);
      renderResult(resultEl, result);
      submitBtn.disabled = true;
      submitBtn.classList.add('cir-submit--done');
      resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (onSubmit) {
        const wrong: CircleExamWrong[] = [];
        // 依「非 printOnly 大題」展平對齊 result.results(scoreCircleExam 略過 printOnly)。
        let flat = 0;
        exam.sections.forEach((section) => {
          if (section.printOnly) return;
          section.questions.forEach((q) => {
            const r = result.results[flat];
            flat += 1;
            if (r && !r.correct) wrong.push({ question: q, sectionKind: section.kind });
          });
        });
        onSubmit(result, wrong);
      }
    },
  });
  submitBtn.classList.add('cir-submit');
  submitBtn.setAttribute('data-testid', 'cir-submit');
  actions.appendChild(submitBtn);

  const doneBtn = academicButton({
    label: '完成',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      onDone();
    },
  });
  doneBtn.classList.add('cir-done');
  actions.appendChild(doneBtn);
}

/** 一題的容器(題號 + 題幹 + 作答:term/judge 選項按鈕、其餘文字輸入)。 */
function renderQuestion(
  sIdx: number,
  qIdx: number,
  q: CircleQuestion,
  answers: CircleExamAnswers,
  kind: CircleSection['kind'],
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'cir-q';
  row.dataset.itemId = q.id;
  row.style.cssText =
    'display:flex;flex-direction:column;gap:8px;padding:12px 14px;' +
    'background:var(--ef-paper-bright,#fff);border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:var(--ef-radius,8px);';

  const top = document.createElement('div');
  top.style.cssText = 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;';
  const num = document.createElement('span');
  num.style.cssText = 'font-weight:700;min-width:1.6em;color:var(--ef-ink);';
  num.textContent = `${qIdx + 1}.`;
  top.appendChild(num);
  const prompt = document.createElement('span');
  prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);line-height:1.6;white-space:pre-wrap;';
  prompt.textContent = q.prompt;
  top.appendChild(prompt);
  row.appendChild(top);

  answers[`${sIdx}:${qIdx}`] = '';

  if ((kind === 'term' || kind === 'judge') && q.choices && q.choices.length > 0) {
    // 選項按鈕(點選即作答,單選)。
    const opts = document.createElement('div');
    opts.className = 'cir-choices';
    opts.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;';
    const buttons: HTMLButtonElement[] = [];
    q.choices.forEach((choice) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cir-choice';
      b.textContent = choice;
      b.style.cssText =
        'padding:6px 14px;font-size:15px;border:1px solid var(--ef-line,#d8d2c4);' +
        'border-radius:999px;background:var(--ef-paper-bright,#fff);color:var(--ef-ink);cursor:pointer;';
      b.addEventListener('click', () => {
        answers[`${sIdx}:${qIdx}`] = choice;
        buttons.forEach((other) => {
          const sel = other === b;
          other.style.background = sel ? 'var(--ef-accent,#2f6b4f)' : 'var(--ef-paper-bright,#fff)';
          other.style.color = sel ? '#fff' : 'var(--ef-ink)';
          other.classList.toggle('cir-choice--selected', sel);
        });
      });
      buttons.push(b);
      opts.appendChild(b);
    });
    row.appendChild(opts);
  } else {
    // 文字輸入(換算 / 圓規概念題)。
    const field = document.createElement('label');
    field.style.cssText =
      'display:flex;align-items:center;gap:6px;font-size:14px;color:var(--ef-sub);';
    const tag = document.createElement('span');
    tag.textContent = '答:';
    field.appendChild(tag);
    const input = document.createElement('input');
    input.type = 'text';
    input.className = `cir-input cir-input--${q.id}`;
    input.autocomplete = 'off';
    input.placeholder = '例:8 公分';
    input.style.cssText =
      'min-width:8em;padding:6px 8px;font-size:15px;border:1px solid var(--ef-line,#d8d2c4);' +
      'border-radius:6px;background:var(--ef-paper-bright,#fff);color:var(--ef-ink);';
    input.addEventListener('input', () => {
      answers[`${sIdx}:${qIdx}`] = input.value;
    });
    field.appendChild(input);
    row.appendChild(field);
  }

  return row;
}

/** printOnly(圓規操作)一題:只顯示題號 + 題幹(不放作答格)。 */
function renderPrintOnlyQuestion(qIdx: number, q: CircleQuestion): HTMLElement {
  const row = document.createElement('div');
  row.className = 'cir-q cir-q--printonly';
  row.dataset.itemId = q.id;
  row.style.cssText =
    'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding:10px 14px;' +
    'background:var(--ef-paper-bright,#fff);border:1px solid var(--ef-line,#d8d2c4);' +
    'border-radius:var(--ef-radius,8px);';
  const num = document.createElement('span');
  num.style.cssText = 'font-weight:700;min-width:1.6em;color:var(--ef-ink);';
  num.textContent = `${qIdx + 1}.`;
  row.appendChild(num);
  const prompt = document.createElement('span');
  prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);line-height:1.6;';
  prompt.textContent = q.prompt;
  row.appendChild(prompt);
  return row;
}

/** 結果區:總分 + 逐題對錯 + 正解(+ 理由,judge/term 有理由時顯示)。 */
function renderResult(el: HTMLElement, result: CircleExamScore): void {
  el.innerHTML = '';
  el.style.display = 'flex';

  const headline = document.createElement('div');
  headline.className = 'cir-score';
  headline.setAttribute('data-testid', 'cir-score');
  headline.style.cssText =
    'font-family:var(--ef-serif);font-weight:700;font-size:24px;color:var(--ef-ink);' +
    'border-top:2px solid var(--ef-line,#d8d2c4);padding-top:14px;';
  headline.textContent = `分數:${result.score} / ${result.total}(答對 ${result.correctCount} / ${result.itemCount} 題)`;
  el.appendChild(headline);

  const note = document.createElement('div');
  note.style.cssText = 'font-size:13px;color:var(--ef-sub);';
  note.textContent = '(圓規操作題請印出後用圓規實際作圖,不列入線上分數。)';
  el.appendChild(note);

  const list = document.createElement('div');
  list.className = 'cir-result-list';
  list.style.cssText = 'display:flex;flex-direction:column;gap:8px;font-size:14px;';
  result.results.forEach((r, idx) => {
    const line = document.createElement('div');
    line.className = `cir-result-line ${r.correct ? 'cir-correct' : 'cir-wrong'}`;
    const mark = r.correct ? '✓' : '✗';
    const chosen = r.chosenLabel || '(未作答)';
    line.style.color = r.correct ? 'var(--ef-ink)' : '#b03030';
    line.textContent = r.correct
      ? `${mark} 第 ${idx + 1} 題:${r.correctLabel}`
      : `${mark} 第 ${idx + 1} 題:你寫「${chosen}」→ 正解「${r.correctLabel}」`;
    list.appendChild(line);
  });
  el.appendChild(list);
}
